import { useEffect, useState } from "react";
import { MonitorDown, Loader2, ExternalLink, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";

// "Bilgisayara / telefona program olarak yükle" düğmesi.
// Chrome/Edge kurulum olayını (beforeinstallprompt) sayfa açılırken bir kez gönderir; olay
// public/index.html içinde React'tan önce yakalanıp window.__gsInstallPrompt'ta saklanır.
// Olay varsa düğme doğrudan kurulum penceresini açar. Tarayıcı izin vermiyorsa (önizleme
// çerçevesi, Safari/Firefox, iPhone, daha önce kapatılmış istem) o tarayıcıya özel net yol gösterilir.

const getPrompt = () => (typeof window !== "undefined" ? window.__gsInstallPrompt || null : null);

function isStandalone() {
  return window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true || !!window.__gsInstalled;
}

function inFrame() {
  try { return window.self !== window.top; } catch { return true; }
}

function detectEnv() {
  if (isStandalone()) return "installed";
  if (inFrame()) return "iframe";
  const ua = navigator.userAgent || "";
  const ios = /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
  if (ios) return "ios";
  if (/Firefox|FxiOS/.test(ua)) return "firefox";
  if (/SamsungBrowser/.test(ua) || /Android/.test(ua)) return "android";
  if (/Edg\//.test(ua)) return "edge";
  if (/Safari/.test(ua) && !/Chrome|Chromium|CriOS|OPR/.test(ua)) return "safari";
  return "chrome";
}

const HINTS = {
  installed: <>Gazi Sepet bu cihazda zaten program olarak yüklü ve açık.</>,
  iframe: <>Site şu an başka bir pencerenin içinde (önizleme) açık; tarayıcılar burada kuruluma izin vermez. Siteyi <b>yeni sekmede açıp</b> orada “Hemen Yükle”ye basın.</>,
  ios: <>iPhone / iPad'de: Safari'de alttaki <b>Paylaş</b> (kare ve yukarı ok) düğmesine, ardından <b>“Ana Ekrana Ekle”</b>ye dokunun.</>,
  safari: <>Mac Safari'de: üst menüden <b>Dosya → “Dock'a Ekle”</b>yi seçin. (Ya da siteyi Google Chrome / Microsoft Edge ile açın.)</>,
  firefox: <>Firefox siteyi program olarak yüklemeyi desteklemiyor. Siteyi <b>Google Chrome</b> veya <b>Microsoft Edge</b> ile açıp “Hemen Yükle”ye basın.</>,
  android: <>Tarayıcı menüsünden (⋮) <b>“Uygulamayı yükle”</b> veya <b>“Ana ekrana ekle”</b>ye dokunun.</>,
  edge: <>Edge'de: adres çubuğundaki <b>yükle simgesine</b> ya da sağ üstteki <b>⋯ → Uygulamalar → “Bu siteyi uygulama olarak yükle”</b>ye tıklayın.</>,
  chrome: <>Chrome kurulum penceresini şu an açmadı (örneğin daha önce kapatıldıysa bir süre göstermez). Adres çubuğunun sağındaki <b>yükle simgesine</b> ya da <b>⋮ menüsü → “Kaydet ve paylaş” → “Uygulama olarak yükle”</b>ye tıklayın.</>,
};

export function InstallAppButton({ testid, testId, className = "", tone = "dark", label = "Hemen Yükle" }) {
  const tid = testid || testId || "install-app-btn";
  const [hasPrompt, setHasPrompt] = useState(() => !!getPrompt());
  const [state, setState] = useState(() => (isStandalone() ? "done" : "idle")); // idle | prompting | done | help
  const [env, setEnv] = useState(null);

  useEffect(() => {
    const onReady = () => { setHasPrompt(true); setState((s) => (s === "help" ? "idle" : s)); };
    const onInstalled = () => { setHasPrompt(false); setState("done"); };
    window.addEventListener("gs-installable", onReady);
    window.addEventListener("gs-installed", onInstalled);
    if (getPrompt()) setHasPrompt(true);
    return () => {
      window.removeEventListener("gs-installable", onReady);
      window.removeEventListener("gs-installed", onInstalled);
    };
  }, []);

  const install = async () => {
    const evt = getPrompt();
    if (evt) {
      setState("prompting");
      try {
        await evt.prompt();
        const { outcome } = await evt.userChoice;
        setState(outcome === "accepted" ? "done" : "idle");
      } catch { setState("idle"); }
      // Bir istem yalnızca bir kez kullanılabilir; tarayıcı yenisini gönderirse tekrar yakalanır.
      window.__gsInstallPrompt = null;
      setHasPrompt(false);
      return;
    }
    setEnv(detectEnv());
    setState("help");
  };

  const hintCls = tone === "dark" ? "text-white/80" : "text-slate-600";
  const text = state === "prompting" ? "Kurulum açılıyor..." : state === "done" ? "Yüklendi" : label;

  return (
    <div className={className} data-install-ready={hasPrompt ? "1" : "0"}>
      <Button onClick={install} disabled={state === "prompting" || state === "done"} data-testid={tid}
        className="rounded-full gap-2 bg-[#FF6B0A] hover:bg-[#E55A00] text-white">
        {state === "prompting" ? <Loader2 className="h-4 w-4 animate-spin" /> : state === "done" ? <CheckCircle2 className="h-4 w-4" /> : <MonitorDown className="h-4 w-4" />}
        {text}
      </Button>
      {state === "help" && env && (
        <div className={`mt-2 text-xs max-w-xs leading-relaxed ${hintCls}`} data-testid={`${tid}-hint`} data-env={env}>
          <p>{HINTS[env]}</p>
          {env === "iframe" && (
            <a href={window.location.href} target="_blank" rel="noopener noreferrer" data-testid={`${tid}-open-tab`}
              className="mt-2 inline-flex items-center gap-1 font-semibold text-[#FF6B0A] underline">
              <ExternalLink className="h-3 w-3" /> Yeni sekmede aç
            </a>
          )}
        </div>
      )}
    </div>
  );
}
